import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApprovalEvent,
  ApprovePaymentRequest,
  CreatePaymentRequest,
  PayPaymentRequest,
  PaymentRequestListRow,
  PaymentRequestStatus,
  PaymentRequestWithApprovals,
  RejectPaymentRequest,
  ReturnPaymentRequest,
} from '@dcc-erp/shared';

import { api } from '../api';

// ============================================================
// Cache-key scope — same pattern as ar.ts/journal-entries.ts.
// Keyed by companyId so DCC/TTOP/RCI switches give independent caches.
// ============================================================
const listKey = (companyId: string | undefined) => ['payment-requests', companyId] as const;
const detailKey = (companyId: string | undefined, id: string | undefined) =>
  ['payment-requests', companyId, id] as const;

export interface PaymentRequestFilters {
  status?: PaymentRequestStatus;
  vendorId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function usePaymentRequests(
  companyId: string | undefined,
  filters: PaymentRequestFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.vendorId) qs.set('vendorId', filters.vendorId);
  if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) qs.set('dateTo', filters.dateTo);
  const search = qs.toString();
  return useQuery({
    queryKey: [...listKey(companyId), filters],
    queryFn: () =>
      api<PaymentRequestListRow[]>(
        `/companies/${companyId}/payment-requests${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function usePaymentRequest(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: detailKey(companyId, id),
    queryFn: () =>
      api<PaymentRequestWithApprovals>(`/companies/${companyId}/payment-requests/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Full append-only audit history for one PR (Component 7). Distinct from the
// live approval rows on the detail (mutable chain state) — this is the immutable
// event stream rendered by the shared AuditLog panel.
export function usePaymentRequestApprovalEvents(
  companyId: string | undefined,
  id: string | undefined,
) {
  return useQuery({
    queryKey: ['payment-requests', companyId, id, 'approval-events'],
    queryFn: () =>
      api<ApprovalEvent[]>(`/companies/${companyId}/payment-requests/${id}/approval-events`),
    enabled: !!companyId && !!id,
  });
}

// ------------------------------------------------------------
// Invalidation helper — every PR mutation can touch the list,
// the detail row, the audit-event stream, and the JV list
// (auto-posting). Centralized so each useXxx hook below stays a
// one-liner. PREFIX keys only — TanStack prefix-matches, so the
// list key already covers the detail + approval-events queries;
// the explicit detail/events invalidations document intent.
// ------------------------------------------------------------
function invalidatePr(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
  id?: string,
) {
  void qc.invalidateQueries({ queryKey: listKey(companyId) });
  if (id) {
    void qc.invalidateQueries({ queryKey: detailKey(companyId, id) });
    // Audit-event stream so the AuditLog panel refreshes after an action.
    void qc.invalidateQueries({
      queryKey: ['payment-requests', companyId, id, 'approval-events'],
    });
  }
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
  void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
}

export function useCreatePaymentRequest(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePaymentRequest) =>
      api<PaymentRequestWithApprovals>(`/companies/${companyId}/payment-requests`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => invalidatePr(qc, companyId),
  });
}

export function useSubmitPaymentRequest(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<PaymentRequestWithApprovals>(`/companies/${companyId}/payment-requests/${id}/submit`, {
        method: 'POST',
      }),
    onSuccess: (_data, id) => invalidatePr(qc, companyId, id),
  });
}

export function useApprovePaymentRequest(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & ApprovePaymentRequest) =>
      api<PaymentRequestWithApprovals>(`/companies/${companyId}/payment-requests/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, vars) => invalidatePr(qc, companyId, vars.id),
  });
}

export function useRejectPaymentRequest(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & RejectPaymentRequest) =>
      api<PaymentRequestWithApprovals>(`/companies/${companyId}/payment-requests/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, vars) => invalidatePr(qc, companyId, vars.id),
  });
}

// Return-for-revision — PENDING → RETURNED. Resubmittable (unlike reject, which
// is terminal). Body is { step, reason }; the route is POST /:id/return.
export function useReturnPaymentRequest(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & ReturnPaymentRequest) =>
      api<PaymentRequestWithApprovals>(`/companies/${companyId}/payment-requests/${id}/return`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, vars) => invalidatePr(qc, companyId, vars.id),
  });
}

export function usePayPaymentRequest(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & PayPaymentRequest) =>
      api<PaymentRequestWithApprovals>(`/companies/${companyId}/payment-requests/${id}/pay`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, vars) => invalidatePr(qc, companyId, vars.id),
  });
}

export function useCancelPaymentRequest(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<PaymentRequestWithApprovals>(`/companies/${companyId}/payment-requests/${id}/cancel`, {
        method: 'POST',
      }),
    onSuccess: (_data, id) => invalidatePr(qc, companyId, id),
  });
}
